// B2 acceptance page (B01 §9.7 contract): exactly TEN shadcn/ui controls, no data binding.
//   - wrappers data-testid="control-N" data-kind="<kind>" in the order
//     button, input, checkbox, switch, select, slider, tabs, dialog, tooltip, datepicker
//   - one admin-only card (data-testid="admin-only") rendered only for role admin
//   - NO useQuery / fetch / $api in this file (ESLint no-restricted-imports + depcruise "playground-no-data"
//     + e2e/bootstrap.spec.ts assert it). All state is local React state.
// Components other than Button come from `pnpm dlx shadcn@4.20.1 add input checkbox switch select slider
// tabs dialog tooltip calendar popover` into packages/ui/src (docs/bootstrap-steps.md step 17).
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@chartvn/ui/button";
import { Input } from "@chartvn/ui/input";
import { Checkbox } from "@chartvn/ui/checkbox";
import { Switch } from "@chartvn/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@chartvn/ui/select";
import { Slider } from "@chartvn/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@chartvn/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@chartvn/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@chartvn/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@chartvn/ui/popover";
import { Calendar } from "@chartvn/ui/calendar";
import { Can } from "../../features/auth/can";

export const Route = createFileRoute("/_authenticated/playground")({
  // The shell reads this for the page heading and the document title (22-COMPOUNDS-SHELL §2.1
  // obligations 1 and 3). Declared per route so the shell never hardcodes one screen's name.
  staticData: { screenName: "Controls" },
  component: PlaygroundPage,
});

function PlaygroundPage() {
  const [clicks, setClicks] = useState(0);
  const [text, setText] = useState("");
  const [checked, setChecked] = useState(false);
  const [on, setOn] = useState(false);
  const [option, setOption] = useState<string | undefined>(undefined);
  const [level, setLevel] = useState([40]);
  const [date, setDate] = useState<Date | undefined>(undefined);

  return (
    // No <main> and no <h1> here: the shell owns both. Two `main` landmarks is invalid HTML and
    // gives a screen-reader user two entries for one page; a second `h1` steals the focus move that
    // obligation 1 aims at the screen's heading.
    <div className="mx-auto max-w-4xl space-y-8">
      <header>
        <p className="text-muted-foreground text-sm">
          Ten placeholder controls. Nothing on this page reads or writes data.
        </p>
      </header>

      <section className="grid gap-6 sm:grid-cols-2" aria-label="Placeholder controls">
        {/* 1 Button */}
        <Field label="Button">
          <Button
            data-testid="control-1"
            data-kind="button"
            onClick={() => setClicks((c) => c + 1)}
          >
            Clicked {clicks} times
          </Button>
        </Field>

        {/* 2 Input */}
        <Field label="Input">
          <Input
            data-testid="control-2"
            data-kind="input"
            placeholder="Type anything"
            value={text}
            onChange={(e) => setText(e.target.value)}
            aria-label="Free text"
          />
        </Field>

        {/* 3 Checkbox */}
        <Field label="Checkbox">
          <div className="flex items-center gap-2">
            <Checkbox
              data-testid="control-3"
              data-kind="checkbox"
              id="pg-checkbox"
              checked={checked}
              onCheckedChange={(v) => setChecked(v === true)}
            />
            <label htmlFor="pg-checkbox" className="text-sm">
              I agree
            </label>
          </div>
        </Field>

        {/* 4 Switch */}
        <Field label="Switch">
          <div className="flex items-center gap-2">
            <Switch
              data-testid="control-4"
              data-kind="switch"
              id="pg-switch"
              checked={on}
              onCheckedChange={setOn}
            />
            <label htmlFor="pg-switch" className="text-sm">
              Notifications {on ? "on" : "off"}
            </label>
          </div>
        </Field>

        {/* 5 Select */}
        <Field label="Select">
          {/* Spread only when set: exactOptionalPropertyTypes forbids an explicit undefined
              on Radix's optional `value` prop (same rule as the Slider). (D-035) */}
          <Select {...(option === undefined ? {} : { value: option })} onValueChange={setOption}>
            <SelectTrigger
              data-testid="control-5"
              data-kind="select"
              className="w-56"
              aria-label="Choose an option"
            >
              <SelectValue placeholder="Choose an option" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="a">Option A</SelectItem>
              <SelectItem value="b">Option B</SelectItem>
              <SelectItem value="c">Option C</SelectItem>
            </SelectContent>
          </Select>
        </Field>

        {/* 6 Slider */}
        <Field label={`Slider (${level[0] ?? 0})`}>
          <div data-testid="control-6" data-kind="slider" className="w-56 py-2">
            <Slider value={level} onValueChange={setLevel} max={100} step={1} aria-label="Level" />
          </div>
        </Field>

        {/* 7 Tabs */}
        <Field label="Tabs">
          <Tabs defaultValue="first" data-testid="control-7" data-kind="tabs" className="w-56">
            <TabsList>
              <TabsTrigger value="first">First</TabsTrigger>
              <TabsTrigger value="second">Second</TabsTrigger>
            </TabsList>
            <TabsContent value="first">First panel</TabsContent>
            <TabsContent value="second">Second panel</TabsContent>
          </Tabs>
        </Field>

        {/* 8 Dialog */}
        <Field label="Dialog">
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="control-8" data-kind="dialog">
                Open dialog
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Placeholder dialog</DialogTitle>
                <DialogDescription>Press Escape to close. Nothing is saved.</DialogDescription>
              </DialogHeader>
            </DialogContent>
          </Dialog>
        </Field>

        {/* 9 Tooltip */}
        <Field label="Tooltip">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="secondary" data-testid="control-9" data-kind="tooltip">
                  Hover me
                </Button>
              </TooltipTrigger>
              <TooltipContent>Tooltips explain, they never store.</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </Field>

        {/* 10 Date picker (Popover + Calendar) */}
        <Field label="Date picker">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                data-testid="control-10"
                data-kind="datepicker"
                className="w-56 justify-start"
              >
                {date
                  ? date.toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })
                  : "Pick a date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar mode="single" selected={date} onSelect={setDate} />
            </PopoverContent>
          </Popover>
        </Field>
      </section>

      <Can permissions={{ admin: ["ping"] }}>
        <section data-testid="admin-only" className="rounded-lg border border-dashed p-4">
          <h2 className="font-medium">Admin area</h2>
          <p className="text-muted-foreground text-sm">
            Visible to the admin role only. The matching API check is GET /api/admin/ping.
          </p>
        </section>
      </Can>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2 rounded-lg border p-4">
      <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">{label}</p>
      {children}
    </div>
  );
}
